/**
 * SimuladorChat — chat shell composed from PersonaHeader + transcript + composer.
 *
 * Auto-scrolls to the latest message and renders a "digitando…" indicator
 * while the patient response is in-flight.
 */
import { useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { ChatComposer } from "./ChatComposer";
import { PersonaHeader } from "./PersonaHeader";
import { TypingIndicatorBubble } from "./TypingIndicatorBubble";
import type {
  ProcedurePublic,
  SimulatorDifficulty,
  SimulatorMessage,
} from "../types";
import type { PersonaSnapshot } from "../content/pools";

export interface SimuladorChatProps {
  persona: PersonaSnapshot;
  procedure: ProcedurePublic;
  difficulty: SimulatorDifficulty;
  messages: SimulatorMessage[];
  isReplying: boolean;
  onBack: () => void;
  onSend: (content: string) => void;
  onEnd: () => void;
  isEnding?: boolean;
  onRequestHint?: () => void;
  hintLoading?: boolean;
  hintPenaltyTotal?: number;
}

export function SimuladorChat({
  persona,
  procedure,
  difficulty,
  messages,
  isReplying,
  onBack,
  onSend,
  onEnd,
  isEnding,
  onRequestHint,
  hintLoading,
  hintPenaltyTotal,
}: SimuladorChatProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Run on next paint so newly appended bubbles are measured.
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages.length, isReplying]);

  return (
    <div className="flex min-h-[calc(100dvh-8.5rem)] flex-1 flex-col overflow-hidden rounded-xl border border-border/50 bg-background">
      <PersonaHeader
        persona={persona}
        procedure={procedure}
        difficulty={difficulty}
        onBack={onBack}
        onEnd={onEnd}
        isEnding={isEnding}
      />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4"
        data-testid="chat-scroll"
      >
        <div className="flex min-h-full flex-col justify-end">
          <div className="space-y-3">
            <AnimatePresence initial={false} mode="popLayout">
              {messages.map((m) => (
                <ChatMessageBubble key={m.seq} message={m} />
              ))}
              {isReplying ? <TypingIndicatorBubble key="typing" /> : null}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <ChatComposer
        onSend={onSend}
        isReplying={isReplying}
        onRequestHint={onRequestHint}
        hintLoading={hintLoading}
        hintPenaltyTotal={hintPenaltyTotal}
      />
    </div>
  );
}
